import * as React from 'react'
import { Text, View, ScrollView } from 'react-native'
import styled from '@theme/styled-components'
import theme from '@theme/Theme'

import Picker from '@components/picker/Picker'
import Animators from '@components/root/Animators'
import ModalList from '@components/modal/ModalList'
import Loader from '@components/loader/Loader'
import Icon from '@components/icon/Icons'

import useI18n from '@i18n/useI18n'
import useReducer from '@store/useReducer'
import * as CommandeStore from '@store/commande/commande'
import * as UserStore from '@store/user/user'

import { FormikHelpers, useFormik } from 'formik'

import { TypeUtilisateur } from '@api/enum'

interface Values {
  alleeCommande: string
  standCommande: string
  espaceCommande: string
  enseigneCommande: string
  enseigneMail: string
  enseigneTelephone?: string
  enseigneNomContact: string
  enseignes: string[]
}

const LocationSearch = ({ form, onForm }: IncidentFormProps) => {
  const init: Values = React.useMemo(
    () => ({
      standCommande: form.standCommande,
      alleeCommande: form.alleeCommande,
      espaceCommande: form.espaceCommande,
      enseigneCommande: form.enseigneCommande,
      enseigneMail: form.enseigneMail,
      enseigneNomContact: form.enseigneNomContact,
      enseigneTelephone: form.enseigneTelephone,
      enseignes: form.enseignes || [],
    }),
    [form.standCommande, form.alleeCommande, form.espaceCommande, form.enseigneCommande, form.enseignes]
  )

  const i18n = useI18n()

  const commandes = useReducer(CommandeStore.store, (s) => s.commandes)

  const onSubmit = (vals: Values, { setSubmitting }: FormikHelpers<Values>) => {
    onForm(vals).finally(() => setSubmitting(false))
  }

  const { values, handleSubmit, setValues, dirty } = useFormik<Values>({
    enableReinitialize: true,
    initialValues: init,
    onSubmit,
  })

  const onSpace = (next: string) => {
    setValues({
      ...values,
      espaceCommande: next,
      alleeCommande: '',
      standCommande: '',
      enseignes: [],
    })
  }

  const onLocation = (next: string) => {
    setValues({
      ...values,
      alleeCommande: next,
      standCommande: '',
      enseignes: [],
    })
  }

  const onStand = (next: string) => {
    const stands = Array.from(
      new Set(
        commandes
          .filter((c) => c.espace === values.espaceCommande && c.allee === values.alleeCommande && c.stand === next)
          .map((c) => c.enseigne)
      )
    )

    const standCommands = stands.length === 1 ? commandes.filter((c) => c.enseigne === stands[0]) : undefined

    // On prend une des commandes avec les infos exposants en priorité, dans le cas ou il y a des
    // commandes orga et expo en même temps
    const selected =
      stands.length === 1 && !!standCommands
        ? standCommands.find((c) => !!c.emailContact && !!c.nomContact && c.enseigne === stands[0]) || standCommands[0]
        : undefined

    const commandeInfo =
      stands.length === 1
        ? {
            enseigneCommande: stands[0],
            enseignes: stands,

            enseigneMail: selected ? selected.emailContact : '',
            enseigneNomContact: selected ? selected.nomContact : '',
            enseigneTelephone: selected ? selected.telephoneContact : '',
          }
        : {
            enseigneCommande: '',
            enseignes: stands,
            enseigneMail: '',
            enseigneNomContact: '',
            enseigneTelephone: '',
          }

    setValues({
      ...values,
      ...commandeInfo,
      standCommande: next,
    })
  }

  React.useEffect(() => {
    if (dirty) {
      // sumbit on change
      handleSubmit()
    }
  }, [values])

  const espaces = React.useMemo(
    () => Array.from(new Set(commandes.map((c) => c.espace))).sort((a, b) => a.localeCompare(b)),
    [commandes]
  )

  const allees = React.useMemo(
    () =>
      Array.from(
        new Set(
          // filter on parent if selected
          commandes.filter((c) => !values.espaceCommande || c.espace === values.espaceCommande).map((c) => c.allee)
        )
      ).sort((a, b) => a.localeCompare(b)),
    [commandes, values.espaceCommande]
  )
  const stands = React.useMemo(
    () =>
      Array.from(
        // filter on parent if selected
        new Set(
          commandes
            .filter(
              (c) =>
                !values.espaceCommande ||
                !values.alleeCommande ||
                (c.allee === values.alleeCommande && c.espace === values.espaceCommande)
            )
            .map((c) => c.stand)
        )
      ).sort((a, b) => a.localeCompare(b)),
    [commandes, values.alleeCommande]
  )

  const spaceOptions = espaces.map((p) => ({
    label: p || i18n.t('screens.incident.COMMAND_SEARCH.unknownSpace'),
    value: p,
  }))
  const spaceOpt = spaceOptions.find((o) => values.espaceCommande === o.value)

  const locOptions = allees.map((p) => ({ label: p || i18n.t('screens.incident.COMMAND_SEARCH.unknownLoc'), value: p }))
  const locOpt = locOptions.find((o) => values.alleeCommande === o.value)

  const subOptions = stands.map((p) => ({
    label: p || i18n.t('screens.incident.COMMAND_SEARCH.unknownSub'),
    value: p,
  }))
  const subOpt = subOptions.find((o) => values.standCommande === o.value)

  const enseigneOptions = Array.from(
    new Set(
      commandes
        .filter(
          (c) =>
            c.espace === values.espaceCommande && c.allee === values.alleeCommande && c.stand === values.standCommande
        )
        .map((c) => c.enseigne)
    )
  ).map((c) => ({ label: c, value: c }))

  const alleeDisabled = !spaceOpt || locOptions.length === 0
  const standDisabled = !locOpt || subOptions.length === 0
  const enseignesDisabled = !subOpt || enseigneOptions.length < 2

  return (
    <Animators>
      <Container>
        {/* position inverted to satisfy picker */}

        {/* enseignes */}
        <ModalList
          data={enseigneOptions}
          value={form.enseignes}
          title={i18n.t('screens.incident.COMMAND_SEARCH.enseignes')}
          onItemSelected={(newEnseignes) =>
            setValues({
              ...values,
              enseignes: newEnseignes,
              enseigneCommande: newEnseignes.length === 1 ? newEnseignes[0] : '',
            })
          }
          disabled={enseignesDisabled}>
          <View>
            <InputContainer disabled={enseignesDisabled}>
              <FieldContainer>
                <Value hasValue={values.enseignes.length > 0}>
                  {values.enseignes.length > 0
                    ? values.enseignes.join(', ')
                    : i18n.t('screens.incident.COMMAND_SEARCH.placeholder')}
                </Value>
              </FieldContainer>

              <IconWrapper>
                <Icon
                  name="arrow-down"
                  size={24}
                  color={enseignesDisabled ? theme.colors.primaryFocused : theme.colors.primary}
                />
              </IconWrapper>
            </InputContainer>
          </View>
        </ModalList>
        <Title disabled={enseignesDisabled}>{i18n.t('screens.incident.COMMAND_SEARCH.enseignes')}</Title>

        {/* stand */}
        <Picker
          key={values.standCommande}
          options={subOptions}
          value={subOpt}
          onChange={(next) => onStand(next ? next.value : '')}
          placeholder={i18n.t('screens.incident.COMMAND_SEARCH.placeholder')}
          disabled={standDisabled}
          isSearchable
        />
        <Title disabled={standDisabled}>{i18n.t('screens.incident.COMMAND_SEARCH.subLocation')}</Title>

        {/* allee */}
        <Picker
          options={locOptions}
          value={locOpt}
          onChange={(next) => onLocation(next ? next.value : '')}
          placeholder={i18n.t('screens.incident.COMMAND_SEARCH.placeholder')}
          disabled={alleeDisabled}
          isSearchable
        />
        <Title disabled={alleeDisabled}>{i18n.t('screens.incident.COMMAND_SEARCH.location')}</Title>

        {/* space */}
        <Picker
          options={spaceOptions}
          value={spaceOpt}
          onChange={(next) => onSpace(next ? next.value : '')}
          placeholder={i18n.t('screens.incident.COMMAND_SEARCH.placeholder')}
          isSearchable
        />
        <Title>{i18n.t('screens.incident.COMMAND_SEARCH.space')}</Title>
      </Container>
    </Animators>
  )
}

const CommandSearch = ({ form, onForm, onNext, fetching }: IncidentFormProps) => {
  const commandes = useReducer(CommandeStore.store, (s) => s.commandes)
  const user = useReducer(UserStore.store, (s) => s.user)

  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    const uniques = commandes.reduce((acc, cur) => {
      const isPresent = acc.find((c) => c.enseigne === cur.enseigne)

      if (!isPresent) {
        acc.push(cur)
      }

      return acc
    }, [] as CommandeResponse[])

    if (!form.enseigneCommande && user && user.typeUtilisateur === TypeUtilisateur.EXPOSANT && uniques.length === 1) {
      // unique enseigne for exposant -> skip this step and select this enseigne

      onForm({
        alleeCommande: uniques[0].allee,
        standCommande: uniques[0].stand,
        espaceCommande: uniques[0].espace,
        enseigneCommande: uniques[0].enseigne,
        enseigneMail: uniques[0].emailContact,
        enseigneTelephone: uniques[0].telephoneContact,
        enseigneNomContact: uniques[0].nomContact,
      })

      if (onNext) {
        onNext()
      }
    } else {
      setLoading(false)
    }
  }, [commandes, user])

  if (loading || fetching) {
    return (
      <Loading>
        <Loader />
      </Loading>
    )
  }

  return (
    <MainContainer>
      <Animators>
        <LocationSearch form={form} onForm={onForm} />
      </Animators>
    </MainContainer>
  )
}

export default CommandSearch

const MainContainer = styled(ScrollView)`
  margin: 0 20px 20px;
  padding-top: ${(props) => (props.theme.breakpoint === 'phone' ? 0 : 50)}px;
  flex: 1;
`

const Loading = styled(View)`
  flex: 1;
  margin: 20px;
  align-items: center;
  justify-content: center;
`

const Container = styled(View)`
  flex-direction: column-reverse;
`

const Title = styled(Text)<{ disabled?: boolean }>`
  ${(props) => props.theme.fonts.Headline};
  opacity: ${(props) => (props.disabled ? 0.2 : 1)};
  margin-bottom: 5px;
  margin-top: 25px;
`

const InputContainer = styled(View)<{ disabled: boolean }>`
  ${(props) => props.disabled && `opacity: 0.6;`}
  background-color: ${(props) => props.theme.colors.background};
  min-height: ${(props) => (props.theme.breakpoint === 'phone' ? 48 : 56)}px;
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? `4px 12px` : '0px 8px 0px 10px')};
  border-bottom-width: 1px;
  border-color: ${(props) => props.theme.colors.primaryFocused};
  flex-direction: row;
  align-items: center;
  ${(props) =>
    props.theme.breakpoint !== 'phone' &&
    `border-radius: 6px;
    border: 2px solid ${props.theme.colors.secondaryActive};
    border-bottom-width: 2px;`}
`

const FieldContainer = styled(View)`
  flex: 1;
`

const Value = styled(Text)<{ hasValue: boolean }>`
  padding: 0;
  ${(props) => props.theme.fonts.Body};
  color: ${(props) => (props.hasValue ? props.theme.colors.secondaryText : props.theme.colors.disableDark)};
  ${(props) => props.theme.breakpoint !== 'phone' && 'font-size: 16px; margin-left:2px;'}
`

const IconWrapper = styled(View)`
  padding: 0 0 0 8px;
`
